import axios, { AxiosError, AxiosRequestHeaders } from "axios";
import { queryClient } from "../queries/client";
import { useClearCredentials, revalidateAuth } from "../utils/auth";
import { useAuthStore } from "../store/useAuthStore";
import { CustomResponse } from "../types/user";

const apiBaseUrl = import.meta.env.VITE_API;
if (!apiBaseUrl) {
  console.warn("VITE_API environment variable is not set, API calls will fail");
}

const api = axios.create({
  baseURL: apiBaseUrl || "http://localhost:4000",
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor for authentication
api.interceptors.request.use((config) => {
  // Revalidate FIRST, then read (possibly cleared) state
  revalidateAuth();

  const authState = useAuthStore.getState();
  const id = authState.id || "";
  const accesstoken = authState.accessToken;

  if (accesstoken && !config.url?.endsWith("/login")) {
    (config.headers as AxiosRequestHeaders).AccessToken = accesstoken;
    (config.headers as AxiosRequestHeaders).id = id;
  }

  if (config.url === "exit") {
    delete (config.headers as AxiosRequestHeaders).AccessToken;
  }

  return config;
});

// Response interceptor for error handling and token refresh
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      useClearCredentials();
      queryClient.clear();
      useAuthStore.setState(() => ({
        isAuthenticated: false,
        accessToken: undefined,
        refreshToken: undefined,
        email: undefined,
        name: undefined,
        id: undefined,
        role: undefined,
        clientId: undefined,
        tokenExpiry: undefined,
      }));
      window.location.href = (import.meta.env.VITE_FRONT || window.location.origin) + "/login";
    }
    if (error.response?.status === 403) {
      // Deactivated mid-session: clear credentials and show the deactivated page
      const errorMessage = (error.response?.data as { errorMessage?: string } | undefined)
        ?.errorMessage;
      if (errorMessage === "ACCOUNT_DEACTIVATED") {
        useClearCredentials();
        queryClient.clear();
        useAuthStore.setState(() => ({
          isAuthenticated: false,
          accessToken: undefined,
          refreshToken: undefined,
          email: undefined,
          name: undefined,
          id: undefined,
          role: undefined,
          clientId: undefined,
          tokenExpiry: undefined,
        }));
        window.location.replace(
          (import.meta.env.VITE_FRONT || window.location.origin) + "/deactivated",
        );
        // Halt the promise chain — the page is navigating away, so no error
        // handlers (toasts, retries) should run
        return new Promise(() => {});
      }
      console.warn("Access forbidden:", error.response?.data);
    }
    return Promise.reject(error);
  },
);

// Standardized response handlers
export const handleResponse = <T>(response: { data: T }): T => response.data;

export const handleCustomResponse = <T>(response: {
  data: CustomResponse<T>;
}): T => {
  if (response.data.success) {
    return response.data.data;
  }
  throw new Error(response.data.message || "Request failed");
};

export const handleApiError = (error: any): string => {
  if (error.response?.data?.message) {
    return error.response.data.message;
  }
  if (error.message) {
    return error.message;
  }
  return "An unexpected error occurred";
};

export default api;
